/**
 * useTurnstile - Turnstile CAPTCHA state management for anonymous users
 *
 * Extracted from useUniversalQuota.ts (2025-12-15) for:
 * - Separation of concerns
 * - Reusability across screens
 * - Testability
 *
 * @module hooks/quota/useTurnstile
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { Platform } from 'react-native';
import { captureEvent } from '../../lib/analytics';
import { getStoredUtmParams } from '../../lib/utm';
import type { TurnstileHandle } from '../../lib/turnstile/turnstileConfig';

export interface UseTurnstileResult {
  /** Current Turnstile token (null if not verified) */
  turnstileToken: string | null;
  /** Set Turnstile token manually */
  setTurnstileToken: (token: string | null) => void;
  /** Current Turnstile error (null if no error) */
  turnstileError: string | null;
  /** Set Turnstile error manually */
  setTurnstileError: (error: string | null) => void;
  /** Handler for successful Turnstile verification */
  handleTurnstileSuccess: (token: string) => void;
  /** Handler for Turnstile verification failure */
  handleTurnstileError: (error: string) => void;
  /** Ref to pass to TurnstileWidget for imperative reset (Phase 2) */
  turnstileRef: React.RefObject<TurnstileHandle | null>;
  /** Request widget reset — silently regenerates token for next analysis */
  requestReset: () => void;
}

interface UseTurnstileOptions {
  /** Whether user is anonymous (affects analytics) */
  isAnonymous: boolean;
  /** Whether analysis data exists (affects captcha_verified event) */
  hasAnalysisData: boolean;
}

/**
 * Cloudflare Turnstile error codes that are transient and safe to auto-retry.
 * Reference: https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/
 *
 * - 110100/110110: Challenge expired (token too old) — retry will get fresh token
 * - 110420: Timeout during challenge — transient, retry should work
 * - 300031: Network error during challenge — transient
 */
const RETRYABLE_ERROR_CODES = new Set([
  '110100', // Challenge expired
  '110110', // Challenge expired (variant)
  '110420', // Challenge timeout
  '300031', // Network error
]);

/** Maximum auto-retry attempts for transient errors (P1 fix: #790) */
const MAX_AUTO_RETRIES = 2;
/** Delay before first retry (ms) */
const RETRY_DELAY_MS = 1500;

/**
 * Hook for managing Turnstile CAPTCHA state
 *
 * @param options - Configuration options
 * @returns Turnstile state and handlers
 *
 * @example
 * ```typescript
 * const {
 *   turnstileToken,
 *   handleTurnstileSuccess,
 *   handleTurnstileError,
 * } = useTurnstile({ isAnonymous: true, hasAnalysisData: false });
 *
 * return (
 *   <TurnstileWidget
 *     onSuccess={handleTurnstileSuccess}
 *     onError={handleTurnstileError}
 *   />
 * );
 * ```
 */
export function useTurnstile({
  isAnonymous,
  hasAnalysisData,
}: UseTurnstileOptions): UseTurnstileResult {
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileHandle | null>(null);
  /** Auto-retry attempt counter (P1 fix: #790 — transient challenge failures) */
  const retryCountRef = useRef<number>(0);
  /** Retry timer handle for cleanup */
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup: clear pending retry timer when component unmounts (prevents memory leak)
  useEffect(() => {
    return () => {
      if (retryTimerRef.current !== null) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, []);

  const handleTurnstileSuccess = useCallback(
    (token: string) => {
      setTurnstileToken(token);
      setTurnstileError(null);

      // Reset retry counter on successful challenge
      retryCountRef.current = 0;
      if (retryTimerRef.current !== null) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }

      // Track CAPTCHA verification for new anonymous users (#795: UTM attribution)
      // NocoDB #901: Renamed from 'trial_started' → 'captcha_verified'.
      // This event fires on Turnstile CAPTCHA success, NOT on trial conversion.
      // Do NOT use this metric for activation funnel analysis.
      // True trial conversion: Trial_Start (typed event) or trial_prompt_* events.
      if (isAnonymous && !hasAnalysisData) {
        // Read UTM params asynchronously — non-blocking, event fires regardless
        getStoredUtmParams().then((utm) => {
          captureEvent('captcha_verified', {
            platform: Platform.OS,
            // UTM attribution (all optional — omitted if not present)
            ...(utm.utm_source && { utm_source: utm.utm_source }),
            ...(utm.utm_medium && { utm_medium: utm.utm_medium }),
            ...(utm.utm_campaign && { utm_campaign: utm.utm_campaign }),
            ...(utm.utm_content && { utm_content: utm.utm_content }),
            ...(utm.utm_term && { utm_term: utm.utm_term }),
          });
        }).catch(() => {
          // Fallback: fire event without UTM if AsyncStorage fails
          captureEvent('captcha_verified', {
            platform: Platform.OS,
          });
        });
      }
    },
    [isAnonymous, hasAnalysisData]
  );

  const handleTurnstileError = useCallback((error: string) => {
    // P1 fix (#790): Auto-retry transient Cloudflare challenge errors before
    // surfacing the error to the user. Errors like expired challenges or
    // network timeouts are temporary and a widget reset resolves them.
    const isRetryable = RETRYABLE_ERROR_CODES.has(error);
    const attemptsLeft = MAX_AUTO_RETRIES - retryCountRef.current;

    if (isRetryable && attemptsLeft > 0) {
      retryCountRef.current += 1;
      console.warn(
        `[useTurnstile] Transient error ${error} — auto-retry ${retryCountRef.current}/${MAX_AUTO_RETRIES} in ${RETRY_DELAY_MS}ms`
      );

      // Silently reset widget after short delay — no error state exposed to user
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        turnstileRef.current?.reset();
      }, RETRY_DELAY_MS);

      return; // Don't expose transient errors to the user or analytics
    }

    // Permanent failure or retries exhausted — surface error
    setTurnstileError(error);
    setTurnstileToken(null);
    retryCountRef.current = 0;

    captureEvent('trial_turnstile_error', {
      error_message: error,
      platform: Platform.OS,
      // Enriched context for debugging (#790)
      error_type: isRetryable ? 'transient_retry_exhausted' : 'permanent',
      retries_attempted: isRetryable ? MAX_AUTO_RETRIES : 0,
    });
  }, []);

  // Phase 2: Reset widget to prefetch next token after analysis
  const requestReset = useCallback(() => {
    setTurnstileToken(null);
    setTurnstileError(null);
    retryCountRef.current = 0; // Reset retry counter on explicit reset
    if (retryTimerRef.current !== null) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    turnstileRef.current?.reset();
  }, []);

  return {
    turnstileToken,
    setTurnstileToken,
    turnstileError,
    setTurnstileError,
    handleTurnstileSuccess,
    handleTurnstileError,
    turnstileRef,
    requestReset,
  };
}
